import { useState, useMemo, useEffect } from 'react'

import {
	EuiModalBody,
	EuiModalFooter,
	EuiButton,
} from '@equipmentshare/ds2'

import AddList from '../../../components/AddList'

import useCompanyId from 'hooks/useCompanyId'
import { useUsersSearchQuery, UsersSearchQuery, Group, User } from '../../../../../../graphql'
import getSortedUsersWithLabel, { UserWithLabel } from '../../helpers/get-sorted-users-with-label'
import useAddUsersHandler from '../../effects/add-users-to-group'
import { userFullName } from 'views/management/permissions/utils'
import useInputDebounce from 'hooks/useInputDebounce'

import { heapEvent } from '@equipmentshare/heaper'

export type AddUsersFormProps = {
	onCancel: () => void,
	onSuccess: () => void,
	onConfirm: () => void,
	selectedGroup: Group,
}

type RawUser = UsersSearchQuery['usersSearch'][number]

const labelRenderer = ( user: {
	first_name: string,
	last_name: string,
	email_address?: string,
} ) => {
	return `${userFullName( user )} (${user.email_address})`
}

export default function AddUsersForm( { onCancel, onSuccess, onConfirm, selectedGroup }: AddUsersFormProps ) {
	const [ isSaving, setIsSaving ] = useState( false )
	const [ selectedUsers, setSelectedUsers ] = useState<UserWithLabel[]>( [] )
	const [ rawUsers, setRawUsers ] = useState<RawUser[]>( [] )
	const [ searchLoading, setSearchLoading ] = useState<boolean>( false )
	const [ noSuggestions, setNoSuggestions ] = useState<boolean>( true )
	const [ searchInput, setSearchInput ] = useState<string>( '' )
	const [ searchString, setSearchString ] = useState<string>( '' )

	const companyId = useCompanyId()
	const { refetch } = useUsersSearchQuery( { variables: { companyId, search: '' }, skip: true } )
	const users = useMemo<UserWithLabel[]>( () => getSortedUsersWithLabel( rawUsers as User[], labelRenderer ), [ rawUsers ] )

	const addUsers = useAddUsersHandler()

	const handleSubmit = async() => {
		heapEvent( 'Inventory - Add Users to Group Modal - Click - Submit', {} )
		setIsSaving( true )
		const result = await addUsers( selectedGroup, selectedUsers )
		setIsSaving( false )
		onConfirm()
		onSuccess()
		return result
	}

	const handleCancel = () => {
		heapEvent( 'Inventory - Add Users to Group Modal - Click - Cancel', {} )
		onCancel()
	}

	useInputDebounce( searchInput, 300, ( debouncedInputValue ) => setSearchString( debouncedInputValue ) )
	const handleSearchChange = ( searchText: string ) => {
		setSearchInput( searchText )
	}

	useEffect( () => {
		setSearchLoading( true )
		if ( searchString ) {
			refetch( { search: searchString, companyId } ).then( ( results ) => {
				setRawUsers( results.data.usersSearch ?? [] )
				setNoSuggestions( false )
			} )
		} else {
			setRawUsers( [] )
			setNoSuggestions( true )
		}
		setSearchLoading( false )
	}, [ searchString ] )

	return (
		<>
			<EuiModalBody>
				<AddList
					id="addList"
					itemList={users}
					itemRender={( user ) => <span>{user.label}</span>}
					getName={( user ) => user.label}
					searchPlaceholder="Search for a user..."
					isLoading={searchLoading}
					selectedItems={selectedUsers}
					setSelectedItems={setSelectedUsers}
					valueKey="user_id"
					onSearchChange={handleSearchChange}
					noSuggestions={noSuggestions}
				/>
			</EuiModalBody>
			<EuiModalFooter>
				<EuiButton
					id="cancelBtn"
					color="text"
					onClick={handleCancel}
				>
					Cancel
				</EuiButton>
				<EuiButton
					id="submitBtn"
					fill
					onClick={handleSubmit}
					isLoading={isSaving}
				>
					Submit
				</EuiButton>
			</EuiModalFooter>
		</>
	)
}
